import { Pan, Zoom, Bounds } from '../diagram.types';
import type { RefObject } from 'preact';
import { useState, useRef } from 'preact/hooks';
import { useDragPan } from './useDragPan';
import { useWheelZoom } from './useWheelZoom';
import { usePinchZoom } from './usePinchZoom';
import {} from '../utils/diagramUtils';
import { PanBounds, getPanBounds, pan, getPanProps, centerXYToPanXY } from '../utils/panUtils';
import { zoom, getZoomProps, getZoomToFitZoom } from '../utils/zoomUtils';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { isTouch } from '#utils/UNSAFE_visUtils/clientUtils';
import { Property } from 'csstype';
import { useVisDragModeControls } from '#hooks/PRIVATE_useVisDragModeControls';

export type PanZoomProps = {
  props: Pan & Zoom;
  captureTargetRef: RefObject<HTMLDivElement>;
  width: number;
  height: number;
  contentBounds: Bounds;
  isRtl: boolean;
};

export const usePanZoom = ({
  props,
  captureTargetRef,
  width,
  height,
  contentBounds,
  isRtl
}: PanZoomProps) => {
  const [cursor, setCursor] = useState<Property.Cursor>();
  const zoomProps = getZoomProps(props, width, height, contentBounds);
  const panBounds = useRef<PanBounds>();
  const prevZoom = useRef<number>();
  const panning = useRef<string>();
  const initPanZoomState = useRef({
    zoom: props.zoomValue || 0,
    centerX: props.centerX,
    centerY: props.centerY
  });

  const isDeviceTouch = isTouch();
  const plotArea = {
    width: width,
    height: height,
    x: 0,
    y: 0
  };

  const showPanButton = isDeviceTouch ? props.panning && props.panning !== 'off' : undefined;
  const dragModeOptions = {
    isZoomScroll: true,
    plotAreaSpace: plotArea,
    componentWidth: width,
    isRtl,
    showPanButton: showPanButton ?? false,
    showZoomButton: false,
    showSelectButton: false,
    isDisabled: !isDeviceTouch
  };

  const { toggleButtonContent, userDragMode } = useVisDragModeControls(dragModeOptions);
  const panProps = getPanProps(props, contentBounds, userDragMode, isDeviceTouch);
  if (
    contentBounds.width !== Infinity &&
    (zoomProps.zoomValue !== prevZoom.current || panProps.panning !== panning.current)
  ) {
    panBounds.current = getPanBounds(
      width,
      height,
      contentBounds,
      zoomProps.zoomValue,
      zoomProps.minZoom,
      initPanZoomState.current,
      panProps.panning
    );
    prevZoom.current = zoomProps.zoomValue;
    panning.current = panProps.panning;
  }
  const dragPanProps = useDragPan({
    panProps: panProps,
    captureTargetRef: captureTargetRef,
    panBounds: panBounds.current,
    zoom: zoomProps.zoomValue,
    width,
    height,
    onDragPanStart: () => setCursor('grabbing'),
    onDragPanEnd: () => setCursor('auto')
  });
  //dZoom or nZoom is required
  const kbZoomCallback = (props: { dZoom?: number; nZoom?: number }) => {
    const newZoom = props.dZoom
      ? zoomProps.zoomValue + props.dZoom
      : getZoomToFitZoom(width, height, contentBounds);
    zoom(
      props.nZoom ? props.nZoom : newZoom,
      zoomProps.zoomValue,
      zoomProps.minZoom,
      zoomProps.maxZoom,
      zoomProps.onZoom
    );
  };
  const prevPanState = useRef({ panX: 0, panY: 0 });

  // dPan or nPan is required
  const kbPanCallback = (props: {
    dPan?: { dx: number; dy: number };
    nPan?: { cx: number; cy: number };
    center?: boolean;
  }) => {
    let centerX;
    let centerY;
    if (props.center) {
      centerX = contentBounds.x + contentBounds.width / 2;
      centerY = contentBounds.y + contentBounds.height / 2;
    } else if (props.dPan) {
      centerX = panProps.centerX + props.dPan.dx;
      centerY = panProps.centerY + props.dPan.dy;
    } else {
      centerX = props.nPan!.cx;
      centerY = props.nPan!.cy;
    }
    const panValues = centerXYToPanXY(centerX, centerY, zoomProps.zoomValue, width, height);
    const { newPan } = pan(
      prevPanState.current,
      panValues,
      panProps.panDirection,
      zoomProps.zoomValue,
      width,
      height,
      panBounds.current,
      panProps.onPan
    );
    prevPanState.current = newPan;
  };
  const wheelZoomProps = useWheelZoom(zoomProps);
  const pinchZoomProps = usePinchZoom({
    captureTargetRef,
    zooming: zoomProps.zooming,
    minZoom: zoomProps.minZoom,
    maxZoom: zoomProps.maxZoom,
    zoomValue: zoomProps.zoomValue,
    onZoom: zoomProps.onZoom
  });
  const panZoomProps = mergeProps(dragPanProps, wheelZoomProps, pinchZoomProps);
  return {
    panZoomProps,
    panProps,
    zoomProps,
    toggleButtonContent,
    cursor,
    centerX: panProps.centerX,
    centerY: panProps.centerY,
    zoomValue: zoomProps.zoomValue,
    kbPanCallback: panProps.panning !== 'off' ? kbPanCallback : undefined,
    kbZoomCallback: zoomProps.zooming !== 'off' ? kbZoomCallback : undefined
  };
};
